"use client";

import { useCallback } from "react";
import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import type { AutocompleteResult, LocationFilter } from "@/lib/api/types";
import type { PresetTypeSlug } from "@/features/presets";
import { BulkModifyDropdown, ClearChipsButton, FilterLabel, PresetButton } from "../shared";
import { FilterLocationInput } from "@/features/search/components/filters/input/location-input";
import { parseLocationResult } from "@/features/search/lib/parse-location-result";

export function FilterLocationsSection({ onOpenPreset }: { onOpenPreset?: (type: PresetTypeSlug) => void }) {
  const { filters, updateFilter } = useTalentSearchFilters();
  const locations = filters.locations ?? [];

  const updateLocation = useCallback(
    (index: number, updated: LocationFilter) => {
      const next = [...locations];
      next[index] = updated;
      updateFilter("locations", next);
    },
    [locations, updateFilter],
  );

  const removeLocation = useCallback(
    (index: number) => {
      updateFilter("locations", locations.filter((_, j) => j !== index));
    },
    [locations, updateFilter],
  );

  const bulkSetScope = useCallback(
    (scope: "c" | "p" | "cp") => {
      updateFilter("locations", locations.map((loc) => ({ ...loc, scope })));
    },
    [locations, updateFilter],
  );

  const bulkExclude = useCallback(() => {
    updateFilter("locations", locations.map((loc) => ({ ...loc, mode: "exclude" as const })));
  }, [locations, updateFilter]);

  const bulkUnexclude = useCallback(() => {
    updateFilter("locations", locations.map((loc) => ({ ...loc, mode: "include" as const })));
  }, [locations, updateFilter]);

  const bulkRequire = useCallback(() => {
    updateFilter("locations", locations.map((loc) => ({ ...loc, required: true })));
  }, [locations, updateFilter]);

  const bulkUnrequire = useCallback(() => {
    updateFilter("locations", locations.map((loc) => ({ ...loc, required: false })));
  }, [locations, updateFilter]);

  return (
    <>
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center justify-between w-full">
          <FilterLabel>Locations</FilterLabel>
          {locations.length > 0 && (
            <BulkModifyDropdown
              onSetScope={bulkSetScope}
              onExcludeAll={bulkExclude}
              onUnexcludeAll={bulkUnexclude}
              onRequireAll={bulkRequire}
              onUnrequireAll={bulkUnrequire}
            />
          )}
        </div>
        <div className="flex flex-col">
          <AutocompleteInput
            variant="filter"
            field="locations"
            placeholder="Search..."
            allowFreeText={false}
            flatBottom
            onSelect={(result: AutocompleteResult) => {
              updateFilter("locations", [
                ...locations,
                { ...parseLocationResult(result), scope: "cp", required: false } as LocationFilter,
              ]);
            }}
          />
          <PresetButton label="Location Presets" onClick={() => onOpenPreset?.("locations")} />
        </div>
      </div>

      {locations.map((loc, i) => (
        <FilterLocationInput
          key={`loc-${loc.display_name ?? loc.region ?? loc.country}-${i}`}
          location={loc}
          defaultDistance={filters.distance}
          onChange={(updated) => updateLocation(i, updated)}
          onRemove={() => removeLocation(i)}
        />
      ))}

      {locations.length > 1 && (
        <div className="flex justify-end">
          <ClearChipsButton onClick={() => updateFilter("locations", undefined)} />
        </div>
      )}
    </>
  );
}
